import { useQuery } from '@tanstack/react-query';
import { authFetch } from '../api/authFetch';
import { admin_apiConfig } from '../config/config';
import { getOrCreateQueryClient } from '../query/createQueryClient';
import { getCurrentUser } from '../../features/auth/api/authUtils';
import { setBusinessTimeZone } from '../time/businessTime';

const CURRENT_USER_ROLE_CACHE_KEY = 'currentUserRoleCache';
const CURRENT_USER_ACCESS_CACHE_KEY = 'currentUserAccessCache';

const cacheRole = (role) => {
  if (!role) {
    try { localStorage.removeItem(CURRENT_USER_ROLE_CACHE_KEY); } catch {}
    return;
  }
  try {
    localStorage.setItem(CURRENT_USER_ROLE_CACHE_KEY, String(role));
  } catch {
    // ignore storage failures
  }
};

const cacheAccess = (access) => {
  if (!access) return;
  try {
    localStorage.setItem(CURRENT_USER_ACCESS_CACHE_KEY, JSON.stringify(access));
  } catch {
    // ignore
  }
};

/**
 * Fetches the current user's RBAC context.
 * The backend is authoritative; production fails closed if it is unavailable.
 */
const loadCurrentUserAccess = async () => {
  const user = await getCurrentUser();
  if (!user) throw new Error('No authenticated user found.');
  const res = await authFetch(`${admin_apiConfig.baseURL}/api/organization/me`);
  if (!res.ok) {
    const error = new Error(`Failed to load authoritative user access (${res.status}).`);
    error.status = res.status;
    throw error;
  }
  const body = await res.json();
  if (!body?.organization_id || !body?.profile_id || !['apps', 'configure', 'administrator'].includes(body?.workspace_access)) {
    throw new Error('Authoritative user access is incomplete.');
  }

  const role = body.role_slug ? String(body.role_slug) : null;
  const workspaceAccess = body.workspace_access;
  const platformTier = workspaceAccess === 'apps' ? 'worker' : 'admin';
  const isSuperadmin = workspaceAccess === 'administrator';
  const access = {
    role,
    roleId: body.role_id || null,
    organizationId: body.organization_id,
    profileId: body.profile_id,
    facilityId: body.facility_id || null,
    workspaceAccess,
    platformTier,
    isSuperadmin,
    reportsToUserProfileId: body.reports_to_user_profile_id || null,
    timeZone: body.time_zone_set ? body.time_zone || null : null,
    timeZoneSet: Boolean(body.time_zone_set),
  };
  setBusinessTimeZone(access.timeZone, access.timeZoneSet);
  cacheRole(role);
  cacheAccess(access);
  return access;
};

const ACCESS_STALE_MS = 15 * 60 * 1000;

/**
 * The current user's access from the shared ['currentUserAccess'] query, so the org id,
 * profile and role queries all wait on one /api/organization/me request. Query functions
 * pass their context, so a test's own client is used instead of the app's.
 */
export const fetchCurrentUserAccess = ({ client } = {}) =>
  (client || getOrCreateQueryClient()).fetchQuery({
    queryKey: ['currentUserAccess'],
    queryFn: loadCurrentUserAccess,
    staleTime: ACCESS_STALE_MS,
    retry: false,
  });

// First paint uses the last known business time zone; the fetch above refreshes it.
try {
  const cached = JSON.parse(localStorage.getItem(CURRENT_USER_ACCESS_CACHE_KEY) || 'null');
  if (cached?.timeZoneSet) setBusinessTimeZone(cached.timeZone, true);
} catch {
  // A missing or unreadable cache just means the zone arrives with the fetch.
}

export const fetchCurrentUserRole = async () => {
  const access = await fetchCurrentUserAccess();
  return access?.role || null;
};

export const useCurrentUserAccess = () =>
  useQuery({
    queryKey: ['currentUserAccess'],
    queryFn: loadCurrentUserAccess,
    staleTime: ACCESS_STALE_MS,
    retry: false,
  });

export const useCurrentUserRole = ({ enabled = true } = {}) =>
  useQuery({
    enabled,
    queryKey: ['currentUserAccess'],
    queryFn: loadCurrentUserAccess,
    staleTime: ACCESS_STALE_MS,
    retry: false,
    select: (data) => data?.role || null,
  });
